"use client";

import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";

export const TypewriterWords = ({
	words,
	duration = 3000,
	typingSpeed = 100,
	pauseDuration = 500,
	className,
}: {
	words: string[];
	duration?: number;
	typingSpeed?: number;
	pauseDuration?: number;
	className?: string;
}) => {
	const [currentWord, setCurrentWord] = useState(words[0] || "");
	const [displayedText, setDisplayedText] = useState("");
	const [isTyping, setIsTyping] = useState(true);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isAnimating, setIsAnimating] = useState(false);

	// Handle the typing and deleting animation
	useEffect(() => {
		if (!isAnimating) {
			return;
		}

		if (isTyping) {
			if (displayedText.length < currentWord.length) {
				const timeout = setTimeout(() => {
					setDisplayedText(currentWord.substring(0, displayedText.length + 1));
				}, typingSpeed);
				return () => clearTimeout(timeout);
			}

			// Finished typing, wait before starting to delete
			const timeout = setTimeout(() => {
				setIsTyping(false);
				setIsDeleting(true);
			}, duration);
			return () => clearTimeout(timeout);
		}

		if (isDeleting) {
			if (displayedText.length > 0) {
				const timeout = setTimeout(() => {
					setDisplayedText(
						displayedText.substring(0, displayedText.length - 1),
					);
				}, typingSpeed / 2);
				return () => clearTimeout(timeout);
			}

			// Finished deleting, move to next word
			setIsDeleting(false);
			setIsAnimating(false);
		}
	}, [
		currentWord,
		displayedText,
		isTyping,
		isDeleting,
		isAnimating,
		duration,
		typingSpeed,
	]);

	// Start the next word animation
	const startNextWord = useCallback(() => {
		const nextWordIndex = (words.indexOf(currentWord) + 1) % words.length;
		setCurrentWord(words[nextWordIndex] || "");
		setDisplayedText("");
		setIsTyping(true);
		setIsDeleting(false);
		setIsAnimating(true);
	}, [currentWord, words]);

	// Start the animation when component mounts or when we need to move to the next word
	useEffect(() => {
		if (!(isAnimating || isDeleting)) {
			const timeout = setTimeout(() => {
				startNextWord();
			}, pauseDuration);
			return () => clearTimeout(timeout);
		}
	}, [isAnimating, isDeleting, pauseDuration, startNextWord]);

	// Initialize animation on mount
	useEffect(() => {
		setIsAnimating(true);
	}, []);

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				className={cn(
					"relative z-10 inline-block text-left text-neutral-900 dark:text-neutral-100",
					className,
				)}
			>
				<span className="inline-block whitespace-nowrap">
					{displayedText}
					<motion.span
						animate={{ opacity: [1, 0] }}
						transition={{ repeat: Number.POSITIVE_INFINITY, duration: 0.8 }}
						className="ml-[1px] inline-block h-[1em] w-[2px] bg-current align-middle"
					/>
				</span>
			</motion.div>
		</AnimatePresence>
	);
};
